import { BasicProfileData, Role } from '@careerhub/api';
import {
  CareerHubModuleReactContext,
  LoadingStates,
  SectionError,
  SectionLoader,
  careerStateSelector,
  userProfileStateSelector,
} from '@careerhub/common';
import {
  Avatar,
  Body1,
  Body1Strong,
  Caption1,
  Card,
  CardFooter,
  CardHeader,
  Divider,
  Link as RedirectLink,
  Subtitle1,
  Text,
  mergeClasses,
} from '@fluentui/react-components';
import { Tag } from '@fluentui/react-tags-preview';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EventName, FeatureName, SubFeatureName } from '../resources/TelemetryEventDetails';
import { useProfileBlockStyles } from './ProfileBlock.styles';
import { IProfileBlockProps } from './ProfileBlock.types';
import { ProfileBlockSkeleton } from './ProfileBlockSkeleton';
import { ErrorContent, ProfileBlockContent } from '../resources/resources';
import { useSelector } from 'react-redux';
import { FeatureFlights } from '../../utilities/FeatureFlights';

// TODO break this into smaller components
/** Banner including personal info and page-specific details */
export const ProfileBlock: React.FC<IProfileBlockProps> = (props: IProfileBlockProps) => {
  const styles = useProfileBlockStyles();
  const { taxonomyTypes, isChatOpen } = useSelector(careerStateSelector);
  const [avatarImage, setAvatarImage] = React.useState<string>();
  const [apiCallLoadingState, setApiCallLoadingState] = React.useState(LoadingStates.NOT_STARTED);
  const [userData, setUserData] = React.useState<BasicProfileData>();
  const [roleData, setRoleData] = React.useState<Role>();
  const { telemetryClient, graphService, roleLibraryService, featureService } =
    React.useContext(CareerHubModuleReactContext);
  const { hrProfile, fetchProfileStatus } = useSelector(userProfileStateSelector);
  const noMatchRoleId = 'NoMatchRoleId';
  const fetchData = async () => {
    try {
      let employeeData: BasicProfileData | undefined;
      if (props.userData) {
        employeeData = props.userData;
      } else {
        employeeData = hrProfile;
      }
      setUserData(employeeData);
      let roleId = employeeData?.standardTitleId || '';
      // Set RoleId to a default role value that does not exist in the role library
      // This is to simulate the experience of a user whose role is not present in the role library
      // This is Temporary and for TESTING only. Should never be enabled in production.
      if (featureService.isFeatureEnabled(FeatureFlights.NoRoleContent)) {
        telemetryClient.logInfo('Role Id set to random role id for testing purposes', { originalRoleId: roleId });
        roleId = noMatchRoleId;
      }
      await roleLibraryService
        .getRoleDetails(roleId)
        .then((roleDetails) => {
          setApiCallLoadingState(LoadingStates.SUCCEEDED);
          setRoleData({ ...roleDetails, categorizedSkills: [], childTaxonomyCount: 0 });
        })
        .catch((error) => {
          if (error.status === 204) {
            telemetryClient.logEvent(FeatureName.Profile, SubFeatureName.NoRoleContent, { roleId: roleId });
            setApiCallLoadingState(LoadingStates.SUCCEEDED);
          } else {
            setApiCallLoadingState(LoadingStates.FAILED);
            console.error(error);
          }
        });

      const photo = await graphService.getMyPhoto();
      setAvatarImage(photo || '');
    } catch (error) {
      setApiCallLoadingState(LoadingStates.FAILED);
      console.error(error);
    }
  };

  useEffect(() => {
    if (userData?.standardTitleId) {
      fetchData();
    } else if (fetchProfileStatus != LoadingStates.NOT_STARTED) {
      setApiCallLoadingState(fetchProfileStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (fetchProfileStatus == LoadingStates.SUCCEEDED) {
      setUserData(hrProfile);
    } else {
      setApiCallLoadingState(fetchProfileStatus);
    }
  }, [fetchProfileStatus, hrProfile]);

  const onViewAllClick: any = () => {
    telemetryClient.logButtonClick(FeatureName.Profile, SubFeatureName.ViewAllRequiredSkills, {
      eventName: EventName.NavigateToRolePage,
    });
  };

  const onReadMoreClick: any = () => {
    telemetryClient.logButtonClick(FeatureName.Profile, SubFeatureName.ReadMoreRoleSummary, {
      eventName: EventName.NavigateToRolePage,
    });
  };

  const { preferredFullName, professionName, disciplineName } = userData || {};
  const level3Type = taxonomyTypes.find((item) => item.level === 3)?.id || 'Role';

  return (
    <SectionLoader
      announce
      errorComponent={
        <SectionError
          title={ErrorContent.sectionErrorTitle}
          description={ErrorContent.sectionErrorDescription}
          className={styles.error}
        />
      }
      state={apiCallLoadingState}
      shimmerComponent={<ProfileBlockSkeleton pageName={props.page} />}
    >
      <div className={mergeClasses(styles.profileContainer, isChatOpen ? styles.profileContainerOnChatOpen : '')}>
        <Card className={mergeClasses(styles.profileCard, isChatOpen ? styles.profileCardOnChatOpen : '')}>
          <CardHeader
            image={
              <Avatar
                name={preferredFullName}
                image={{
                  src: avatarImage,
                }}
                size={48}
              />
            }
            style={{ display: 'flex' }}
            header={<Body1Strong>{preferredFullName}</Body1Strong>}
          />
          <Divider className={styles.divider} />
          <div className={styles.textContents}>
            <Body1Strong>Profession</Body1Strong>
            <Caption1>{professionName || disciplineName}</Caption1>
          </div>
          <div className={styles.textContents}>
            <Body1Strong>Discipline</Body1Strong>
            <Caption1>{disciplineName}</Caption1>
          </div>
          <div className={styles.textContents}>
            <Body1Strong>Role</Body1Strong>
            <Caption1>{roleData?.name || 'Unknown role'}</Caption1>
          </div>
        </Card>
        {/* TODO: move page specific code to their own componenets */}
        {props.page == 'RoleLibraryPage' && (
          <Card className={mergeClasses(styles.roleContainer, isChatOpen ? styles.roleContainerOnChatOpen : '')}>
            {roleData ? (
              <>
                <Card
                  className={mergeClasses(
                    styles.innerCards,
                    isChatOpen ? styles.innerCardWithChat : styles.innerCardWithOutChat
                  )}
                >
                  <CardHeader className={styles.header} header={<Body1Strong>Your current role summary</Body1Strong>} />
                  <Divider className={styles.divider} />
                  <div className={styles.body}>
                    <div className={styles.textContents}>
                      <Body1Strong>{roleData?.name}</Body1Strong>
                      <Caption1 wrap>{roleData?.description}</Caption1>
                    </div>
                  </div>
                  <CardFooter className={styles.footer}>
                    <Link
                      className={styles.link}
                      to={`/${level3Type}/${roleData?.id}`}
                      onClick={onReadMoreClick}
                      aria-label="Read more about role summary"
                    >
                      Read more
                    </Link>
                  </CardFooter>
                </Card>
                <Card
                  className={mergeClasses(
                    styles.innerCards,
                    isChatOpen ? styles.innerCardWithChat : styles.innerCardWithOutChat
                  )}
                >
                  <CardHeader
                    className={styles.header}
                    header={<Body1Strong>Critical skills for your role</Body1Strong>}
                  />
                  <Divider className={styles.divider} />
                  <div className={styles.body}>
                    <div className={styles.container}>
                      {roleData?.criticalSkills?.length ? (
                        roleData.criticalSkills.map((skill) => (
                          <Tag key={skill.id} shape="circular" className={styles.tagItem} size="small">
                            <div className={styles.tagContent}>{skill.name}</div>
                          </Tag>
                        ))
                      ) : (
                        <div>Data not available</div>
                      )}
                    </div>
                  </div>
                  <CardFooter className={styles.footer}>
                    <Link
                      className={styles.link}
                      to={`/${level3Type}/${roleData?.id}#skills`}
                      onClick={onViewAllClick}
                      aria-label="View all critical skills"
                    >
                      View details
                    </Link>
                  </CardFooter>
                </Card>
              </>
            ) : (
              <Text style={{ fontSize: 18, textAlign: 'center' }} weight="semibold">
                {ProfileBlockContent.noRoleContent}
              </Text>
            )}
          </Card>
        )}
        {props.page == 'CareerPlanPage' && (
          <Card className={styles.frameworkStatementContainer}>
            <div className={styles.FrameworkInnerContainer}>
              <CardHeader header={<Subtitle1>The Microsoft career framework</Subtitle1>} />
              <Body1 wrap>
                The Microsoft Career Framework is designed to help you connect the dots within your career experience.
                You are welcome to create your own approach to planning, learning, and realizing your career aspirations
                through the framework. Read about each career pillar below and reference the career journey map to
                explore recommended career resources to get you started.
              </Body1>
              <CardFooter className={styles.footer}>
                <RedirectLink
                  className={styles.link}
                  href={'https://microsoft.sharepoint.com/sites/HRweb/SitePages/CareerDiscover.aspx'}
                >
                  Discover
                </RedirectLink>
                <Text className={styles.linkDivider}> | </Text>
                <RedirectLink
                  className={styles.link}
                  href={'https://microsoft.sharepoint.com/sites/HRweb/SitePages/CareerConnect.aspx'}
                >
                  Connect
                </RedirectLink>
                <Text className={styles.linkDivider}> | </Text>
                <RedirectLink
                  className={styles.link}
                  href={'https://microsoft.sharepoint.com/sites/HRweb/SitePages/CareerGrow.aspx'}
                >
                  Grow
                </RedirectLink>
              </CardFooter>
            </div>
          </Card>
        )}
      </div>
    </SectionLoader>
  );
};
